import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, UserPlus } from "lucide-react";

const inputCls = "w-full px-3 py-2 rounded-lg border-2 border-slate-200 outline-none focus:ring-2 focus:ring-brand-red";
const ROLE_LABEL = { STAFF: "Osoblje", MANAGER: "Menadžer", OWNER: "Vlasnik", PLATFORM_ADMIN: "Admin" };

export function TeamManager({ rid, actorRole }) {
  const canOwner = actorRole === "PLATFORM_ADMIN";
  const roleOptions = canOwner ? ["STAFF", "MANAGER", "OWNER"] : ["STAFF", "MANAGER"];
  const [data, setData] = useState({ members: [], invites: [] });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("STAFF");
  const load = async () => { try { const r = await api.get(`/restaurants/${rid}/team`); setData(r.data); } catch { toast.error("Greška pri učitavanju tima"); } };
  useEffect(() => { load(); }, [rid]);

  const invite = async () => {
    if (!email.trim()) return;
    try { const { data: res } = await api.post(`/restaurants/${rid}/team`, { email, role }); toast.success(res.status === "assigned" ? "Član dodat" : "Pozivnica poslata"); setEmail(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Greška"); }
  };
  const changeRole = async (uid, newRole) => {
    try { await api.patch(`/restaurants/${rid}/team/${uid}`, { role: newRole }); toast.success("Uloga izmenjena"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Greška"); }
  };
  const revoke = async (uid) => {
    if (!window.confirm("Ukloniti pristup ovom članu?")) return;
    try { await api.delete(`/restaurants/${rid}/team/${uid}`); toast.success("Pristup uklonjen"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Greška"); }
  };
  const cancelInvite = async (em) => { await api.delete(`/restaurants/${rid}/invites?email=${encodeURIComponent(em)}`); load(); };

  return (
    <div data-testid="team-manager">
      <div className="flex flex-wrap gap-2 mb-5 bg-white rounded-xl border p-3">
        <input data-testid="team-email" className={inputCls + " flex-1 min-w-[200px]"} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@osobe.com" />
        <select data-testid="team-role" className="px-3 py-2 rounded-lg border-2 border-slate-200" value={role} onChange={(e) => setRole(e.target.value)}>
          {roleOptions.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
        </select>
        <button data-testid="team-invite" onClick={invite} className="bg-brand-red hover:bg-brand-redHover text-white rounded-lg px-4 py-2 font-semibold flex items-center gap-1"><UserPlus size={16} />Pozovi / Dodaj</button>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <p className="text-xs font-bold text-slate-500 uppercase px-4 pt-3">Članovi</p>
        {data.members.length === 0 && <p className="text-sm text-slate-400 p-4">Još nema članova.</p>}
        {data.members.map((m) => (
          <div key={m.user_id} data-testid={`team-member-${m.email}`} className="flex items-center gap-3 p-3 border-b last:border-0">
            <div className="flex-1 min-w-0"><p className="font-semibold truncate">{m.name || "—"}</p><p className="text-xs text-slate-400">{m.email}</p></div>
            <select data-testid={`team-member-role-${m.email}`} value={m.role} onChange={(e) => changeRole(m.user_id, e.target.value)}
              className="px-2 py-1.5 rounded-lg border-2 border-slate-200 text-sm">
              {roleOptions.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              {!roleOptions.includes(m.role) && <option value={m.role}>{ROLE_LABEL[m.role] || m.role}</option>}
            </select>
            <button data-testid={`team-revoke-${m.email}`} onClick={() => revoke(m.user_id)} className="text-brand-red"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      {data.invites.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden mt-4">
          <p className="text-xs font-bold text-slate-500 uppercase px-4 pt-3">Pozivnice na čekanju</p>
          {data.invites.map((i) => (
            <div key={i.email} className="flex items-center justify-between p-3 border-b last:border-0">
              <span className="text-sm text-slate-500">{i.email} · {ROLE_LABEL[i.role] || i.role}</span>
              <button onClick={() => cancelInvite(i.email)} className="text-brand-red"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TeamPage() {
  const { restaurant, role } = useOutletContext();
  return (
    <div className="max-w-2xl">
      <h1 className="font-display font-black text-2xl mb-5">Tim · {restaurant.name}</h1>
      <TeamManager rid={restaurant.id} actorRole={role} />
    </div>
  );
}
